import { useTranslation } from 'react-i18next';

import {
  activeFilterCount,
  type CatalogFilters,
  type CatalogSort,
  type Offered,
  type OwnerFilter,
  type SpaceFilter,
  type TypeFilter,
} from '@/entities/catalog';
import { Button, Combobox, FilterPopover, Icons } from '@/shared/ui';

import styles from './CatalogToolbar.module.css';

interface Props {
  query: string;
  onQuery: (query: string) => void;
  searchLabel: string;
  filters: CatalogFilters;
  onChange: (patch: Partial<CatalogFilters>) => void;
  onReset: () => void;
  /** Which filters this page offers (a workspace section already is a type). */
  offered: Offered;
  /** The orders this page offers, and the one in force. */
  sorts: readonly CatalogSort[];
  sort: CatalogSort;
  /** How many resources each type holds under the OTHER filters and the search (shown as badges). */
  counts?: Partial<Record<TypeFilter, number>>;
}

function Choice<T extends string>({
  legend,
  name,
  value,
  options,
  onPick,
}: {
  legend: string;
  name: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onPick: (value: T) => void;
}) {
  return (
    <fieldset className={styles.group}>
      <legend className={styles.legend}>{legend}</legend>
      <div className={styles.choices}>
        {options.map((option) => (
          <label key={option.value} className={styles.choice}>
            <input
              type="radio"
              name={name}
              checked={value === option.value}
              onChange={() => {
                onPick(option.value);
              }}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * ONE toolbar for every resource catalog (home dashboard, workspace): the search, the "Filters"
 * popover (type, favorites, owner, workspace kind - what the page offers), the sort, and - below -
 * one removable chip per active popover filter (the quick toggles show their own state) with a compact reset. Filtering and sorting are separate
 * controls; changing one never touches another. On a phone the filters collapse into the popover.
 */
export function CatalogToolbar({
  query,
  onQuery,
  searchLabel,
  filters,
  onChange,
  onReset,
  offered,
  sorts,
  sort,
  counts,
}: Props) {
  const { t } = useTranslation();
  // The type has a control of its own on the page (below), so the popover does not repeat it.
  const inPopover = activeFilterCount(filters, {
    ...offered,
    type: false,
    favorites: false,
    recent: false,
    space: false,
  });
  const sortNames: Record<CatalogSort, string> = {
    manual: t('catalog.sortManual'),
    opened: t('catalog.sortOpened'),
    updated: t('catalog.sortUpdated'),
    name: t('catalog.sortName'),
  };
  const typeNames: Record<TypeFilter, string> = {
    all: t('catalog.typeAll'),
    board: t('catalog.typeBoard'),
    database: t('catalog.typeDatabase'),
  };
  const ownerNames: Record<OwnerFilter, string> = {
    any: t('catalog.ownerAny'),
    mine: t('catalog.ownerMine'),
    others: t('catalog.ownerOthers'),
  };
  const spaceNames: Record<SpaceFilter, string> = {
    any: t('catalog.spaceAny'),
    personal: t('catalog.spacePersonal'),
    team: t('catalog.spaceTeam'),
  };
  const chips: { key: string; name: string; clear: Partial<CatalogFilters> }[] = [];
  if (offered.owner && filters.owner !== 'any') {
    chips.push({
      key: 'owner',
      name: `${t('catalog.owner')}: ${ownerNames[filters.owner]}`,
      clear: { owner: 'any' },
    });
  }

  return (
    <div className={styles.root} data-testid="catalog-toolbar">
      <div className={styles.bar} role="group" aria-label={t('catalog.toolbar')}>
        <div className={styles.searchWrap} role="search">
          <Icons.search />
          <input
            type="search"
            className={styles.search}
            value={query}
            maxLength={100}
            placeholder={searchLabel}
            aria-label={searchLabel}
            onChange={(event) => {
              onQuery(event.target.value);
            }}
          />
        </div>
        <FilterPopover activeCount={inPopover} onReset={onReset}>
          {offered.owner && (
            <Choice
              legend={t('catalog.owner')}
              name="catalog-owner"
              value={filters.owner}
              options={(['any', 'mine', 'others'] as const).map((value) => ({
                value,
                label: ownerNames[value],
              }))}
              onPick={(owner) => {
                onChange({ owner });
              }}
            />
          )}
        </FilterPopover>
        <Combobox
          className={styles.sort}
          label={t('catalog.sort')}
          value={sort}
          size="sm"
          options={sorts.map((value) => ({ value, label: sortNames[value] }))}
          onChange={(next) => {
            const chosen = sorts.find((value) => value === next);
            if (chosen) onChange({ sort: chosen });
          }}
        />
      </div>
      {offered.type && (
        <div
          className={styles.segmented}
          role="radiogroup"
          aria-label={t('catalog.type')}
          data-testid="catalog-type"
        >
          {(['all', 'board', 'database'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={filters.type === value}
              className={styles.segment}
              data-type={value}
              onClick={() => {
                onChange({ type: value });
              }}
            >
              <span>{typeNames[value]}</span>
              {counts?.[value] !== undefined && (
                <span
                  className={styles.segmentCount}
                  aria-label={t('catalog.typeCount', { count: counts[value] })}
                >
                  {counts[value]}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      <div className={styles.quick} role="group" aria-label={t('catalog.quick')}>
        {offered.favorites && (
          <button
            type="button"
            className={styles.toggle}
            aria-pressed={filters.favorites}
            onClick={() => {
              onChange({ favorites: !filters.favorites });
            }}
          >
            <span aria-hidden>★</span> {t('catalog.favorites')}
          </button>
        )}
        {offered.recent && (
          <button
            type="button"
            className={styles.toggle}
            aria-pressed={filters.recent}
            onClick={() => {
              onChange({ recent: !filters.recent });
            }}
          >
            <Icons.history /> {t('catalog.recent')}
          </button>
        )}
        {offered.space &&
          (['personal', 'team'] as const).map((space) => (
            <button
              key={space}
              type="button"
              className={styles.toggle}
              aria-pressed={filters.space === space}
              onClick={() => {
                onChange({ space: filters.space === space ? 'any' : space });
              }}
            >
              {spaceNames[space]}
            </button>
          ))}
      </div>
      {activeFilterCount(filters, offered) > 0 && (
        <div className={styles.chipRow}>
          {chips.length > 0 && (
            <ul className={styles.chips} aria-label={t('catalog.chips')}>
              {chips.map((chip) => (
                <li key={chip.key}>
                  <button
                    type="button"
                    className={styles.chip}
                    aria-label={t('catalog.removeChip', { name: chip.name })}
                    onClick={() => {
                      onChange(chip.clear);
                    }}
                  >
                    <span>{chip.name}</span>
                    <Icons.close />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Button size="sm" variant="ghost" onClick={onReset}>
            {t('catalog.resetAll')}
          </Button>
        </div>
      )}
    </div>
  );
}
